/**
 * Compare Screen — base vs scenario deltas for the key deal metrics.
 *
 * Reads scenario/compare (which diffs the two result sets on disk). Pick which
 * scenario to compare from the dropdown; defaults to the active one.
 */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { backendPy } from "System/system";

const fmt = (n, unit) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  if (unit === "pct") return `${(n * 100).toFixed(2)}%`;
  if (unit === "count") return Math.round(n).toLocaleString();
  const a = Math.abs(n);
  const s = a >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : a >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : n.toFixed(2);
  return s;
};
const deltaColor = (d) => (d > 0 ? "#1B7A4A" : d < 0 ? "#A61B2B" : "var(--ev-text-muted)");

const Compare = () => {
  const project = useSelector((s) => s.project);
  const active = useSelector((s) => s.data.activeScenario) || "base";
  const scenarios = useMemo(() => project.scenarios || [], [project.scenarios]);

  const initial = active !== "base" ? active : scenarios.find((s) => s.status === "ready")?.id || "";
  const [scenarioId, setScenarioId] = useState(initial);
  const [compare, setCompare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!scenarioId) return;
    setLoading(true);
    setError(null);
    setCompare(null);
    backendPy("scenario", "compare", { projectid: project.id, scenarioId })
      .then((r) => setCompare(r.data))
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [scenarioId, project.id]);

  if (scenarios.length === 0)
    return (
      <div className="notification is-info is-light">
        No scenarios yet. Ask the assistant to run a what-if (e.g. "remove all EUR invoices and re-run"),
        then come back here to compare it against the original.
      </div>
    );

  const scenarioMeta = scenarios.find((s) => s.id === scenarioId);
  const metrics = (compare?.metrics || []).filter((m) => m.base !== 0 || m.scenario !== 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--ev-text)" }}>
          Compare to Original
        </div>
        <div className="select is-small">
          <select value={scenarioId} onChange={(e) => setScenarioId(e.target.value)}>
            {scenarios.map((s) => (
              <option key={s.id} value={s.id}>{s.name || s.id}{s.status !== "ready" ? ` (${s.status})` : ""}</option>
            ))}
          </select>
        </div>
      </div>

      {scenarioMeta?.status && scenarioMeta.status !== "ready" && (
        <div className="notification is-warning is-light" style={{ fontSize: "0.82rem" }}>
          This scenario is <strong>{scenarioMeta.status}</strong> — figures may be incomplete.
        </div>
      )}

      {loading && <div style={{ fontStyle: "italic", color: "var(--ev-text-muted)" }}>Comparing…</div>}
      {error && <div className="notification is-danger is-light" style={{ fontSize: "0.82rem" }}>{error}</div>}

      {!loading && compare && (
        <>
          <div className="table-container">
            <table className="table is-fullwidth is-striped is-bordered is-small-table" style={{ fontSize: "0.8rem" }}>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th style={{ textAlign: "right" }}>Original</th>
                  <th style={{ textAlign: "right" }}>Scenario</th>
                  <th style={{ textAlign: "right" }}>Δ</th>
                  <th style={{ textAlign: "right" }}>Δ%</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m) => (
                  <tr key={m.key}>
                    <td style={{ whiteSpace: "nowrap" }}>{m.label}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmt(m.base, m.unit)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{fmt(m.scenario, m.unit)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap", color: deltaColor(m.delta), fontWeight: 600 }}>
                      {fmt(m.delta, m.unit)}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap", color: deltaColor(m.delta), fontWeight: 700 }}>
                      {m.deltaPct === null || m.deltaPct === undefined ? "—" : `${m.deltaPct > 0 ? "+" : ""}${m.deltaPct}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {compare?.missing?.length > 0 && (
            <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", marginTop: "0.5rem" }}>
              Not available for comparison: {compare.missing.join(", ")}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Compare;
